import { useState } from 'react';
import { BurgerMenu } from 'components/BurgerMenu';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { HeaderParameters } from '../components';
import { toSnakeCase } from 'utils';
import { useCompany, useCompanyFunctions } from 'contexts/CompanyContext';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import BankFields, { bankValidationSchema, bankFieldsInitialValues } from 'components/BankFields';
import creditCard from '_assets/parameters/credit-card.svg';
import DefaultButton from 'components/DefaultButton';
import InsuranceFields, { insuranceValidationSchema, insuranceFieldsInitialValues } from 'components/InsuranceFields';
import insurance from '_assets/parameters/insurance.svg';
import separator from '_assets/parameters/separator.svg';
import useStyles from './styles';

const fullValidationSchema = bankValidationSchema.concat(insuranceValidationSchema);

const BankParameters = () => {
  const classes = useStyles();

  const { currentCompany } = useCompany();
  const { editCompany, editCompanyDocument } = useCompanyFunctions();

  const [bankDocument, setBankDocument] = useState({
    file: null,
    url: currentCompany?.bankURL ?? null,
  });
  const [insuranceDocument, setInsuranceDocument] = useState({
    file: null,
    url: currentCompany?.insuranceURL ?? null,
  });

  const [isEditingBank, setIsEditingBank] = useState(false);
  const [isEditingInsurance, setIsEditingInsurance] = useState(false);

  function formikValidationSchema(editBank = false, editInsurance = false) {
    if (editBank && editInsurance) return fullValidationSchema;
    if (editBank) return bankValidationSchema;
    if (editInsurance) return insuranceValidationSchema;
    return Yup.object();
  }

  const formik = useFormik({
    initialValues: {
      ...bankFieldsInitialValues(currentCompany),
      ...insuranceFieldsInitialValues(currentCompany),
    },
    validationSchema: formikValidationSchema(isEditingBank, isEditingInsurance),
    onSubmit: async (values) => {
      if (!currentCompany || (!isEditingBank && !isEditingInsurance)) return;

      let documents = [];
      if (bankDocument?.file) {
        let formData = new FormData();
        formData.append('file', bankDocument?.file);
        documents.push({
          data: formData,
          name: 'bank',
          url: bankDocument?.url,
        });
      }

      if (insuranceDocument?.file) {
        let formData = new FormData();
        formData.append('file', insuranceDocument?.file);
        documents.push({
          data: formData,
          name: 'insurance',
          url: insuranceDocument?.url,
        });
      }

      const { edited } = await editCompany(currentCompany, toSnakeCase(values), documents);

      if (edited) {
        setIsEditingBank(false);
        setIsEditingInsurance(false);
        formik.resetForm({ values: { ...values } });
      }
    },
  });

  function toggleEditBank() {
    setIsEditingBank((wasEditingBank) => {
      if (wasEditingBank) {
        formik.resetForm({
          values: bankFieldsInitialValues(currentCompany),
        });
        setBankDocument({ file: null, url: currentCompany?.bankURL ?? null });
      }

      return !wasEditingBank;
    });
  }

  function toggleEditInsurance() {
    setIsEditingInsurance((wasEditingAssurance) => {
      if (wasEditingAssurance) {
        formik.resetForm({
          values: insuranceFieldsInitialValues(currentCompany),
        });
        setInsuranceDocument({ file: null, url: currentCompany?.insuranceURL ?? null });
      }

      return !wasEditingAssurance;
    });
  }

  function handleCancel() {
    if (formik.isSubmitting) return;

    setIsEditingBank(false);
    setIsEditingInsurance(false);

    setBankDocument({ file: null, url: currentCompany?.bankURL ?? null });
    setInsuranceDocument({ file: null, url: currentCompany?.insuranceURL ?? null });

    formik.resetForm();
  }

  async function handleBankDocumentChange(file, url) {
    // If bank data have already been set
    if (formik.initialValues.bankName && formik.initialValues.iban) {
      setBankDocument({ file: null, url });
      const formData = new FormData();
      formData.append('file', file);
      return await editCompanyDocument(currentCompany?.id, 'bank', formData, url);
    }

    setBankDocument({ file, url });
  }

  async function handleInsuranceDocumentChange(file, url) {
    // If insurance data have already been set
    if (formik.initialValues.insuranceName && formik.initialValues.insuranceNumber) {
      setInsuranceDocument({ file: null, url });
      const formData = new FormData();
      formData.append('file', file);
      return await editCompanyDocument(currentCompany?.id, 'insurance', formData, url);
    }

    setInsuranceDocument({ file, url });
  }

  return (
    <Grid className={classes.root} container justifyContent="center" spacing={3}>
      <HeaderParameters noMargin text="Banque et Assurance" />

      <FormikProvider value={formik}>
        <Form style={{ display: 'contents' }}>
          <Grid container item xs={5}>
            <Grid container alignItems="center" style={{ height: '120px' }}>
              <Grid item xs={4}>
                <img src={creditCard} alt="Bank logo" width={100} />
              </Grid>
              <Grid item xs={6}>
                <Typography className={classes.subtitle}>Votre banque</Typography>
              </Grid>
              <Grid container item xs={2} justifyContent="flex-end">
                <BurgerMenu>
                  <BurgerMenu.Item onClick={toggleEditBank}>Modifier</BurgerMenu.Item>
                </BurgerMenu>
              </Grid>
            </Grid>

            <Grid container item xs={12}>
              <BankFields
                document={bankDocument}
                isEditing={isEditingBank}
                onDocumentChange={handleBankDocumentChange}
              />
            </Grid>
          </Grid>

          <Grid container item xs={1} justifyContent="center">
            <img src={separator} alt=" separator" />
          </Grid>

          <Grid container item xs={5}>
            <Grid container alignItems="center" style={{ height: '120px' }}>
              <Grid item xs={4}>
                <img src={insurance} alt="Info Logo" width={100} />
              </Grid>
              <Grid item xs={6}>
                <Typography className={classes.subtitle}>Votre assurance</Typography>
              </Grid>
              <Grid container item xs={2} justifyContent="flex-end">
                <BurgerMenu>
                  <BurgerMenu.Item onClick={toggleEditInsurance}>Modifier</BurgerMenu.Item>
                </BurgerMenu>
              </Grid>
            </Grid>

            <Grid className={classes.insuranceContainer} container item xs={12}>
              <InsuranceFields
                document={insuranceDocument}
                isEditing={isEditingInsurance}
                onDocumentChange={handleInsuranceDocumentChange}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} justifyContent="center">
            <Grid container item xs={12} alignItems="flex-end" justifyContent="center" style={{ marginBottom: '16px' }}>
              {(isEditingBank || isEditingInsurance) && (
                <>
                  <DefaultButton cancel onClick={handleCancel}>
                    Annuler
                  </DefaultButton>
                  <DefaultButton color="secondary" disabled={formik.isSubmitting} type="submit" variant="contained">
                    {formik.isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
                  </DefaultButton>
                </>
              )}
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </Grid>
  );
};

export default BankParameters;
